import axios from 'axios';
import cache, { del as delCache } from '@/helpers/cache';
import { Order } from '@/models/order';
import PackageHelper, { Package } from '@/models/package';
import { Coupon } from '@/models/coupon';
import { Pagination } from '@/models/pagination';
import { View } from '@/models/view';
import { DataReq } from '@/components/Pivot/views/view';
import { SaveDataFile } from '@/helpers/SaveDataFile';
import { Bundle } from '@/models/bundle';
import { Permission } from '@/models/permission';
import { ServiceMatrix, ServiceMatrixUser } from '@/models/serviceMatrix';

// auth http client using firebase token
const axAuth = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: true,

  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
});

const dataOnly = (v: any) => v.data.data;

export interface PivotReq {
  filter: any;
}

export interface OrderListFilter {
  from: number; // unix from
  to: number; // unix to
  filters?: {
    ids?: number[]
    states?: string[]
    account_ids?: number[]
    sales_agent_ids?: number[]
  };

  pagination?: Pagination;
}

export interface InvoiceListFilter {
  from: number; // unix from
  to: number; // unix to
  filters?: {
    ids?: number[]
    states?: string[]
    account_ids?: number[]
    sales_agent_ids?: number[]
  };

  pagination?: Pagination;
}

export interface SubscriptionListFilter {
  from: number; // unix from
  to: number; // unix to
  filters?: {
    ids?: number[]
    states?: string[]
    account_ids?: number[]
    sales_agent_ids?: number[]
  };

  pagination?: Pagination;
}

export default {
  http: axAuth,

  setToken(token: string) {
    this.http.defaults.headers.Authorization = 'token ' + token;
  },

  async getOrderInfo(req?: object) {
    const key = '/order/info[0]';
    return dataOnly(
      await cache(key, 60 * 5, () => this.http.get('/order/info', req || {})),
    );
  },

  async listPackages(): Promise<Package[]> {
    const key = '/admin/packages/list';
    return dataOnly(
      await cache(key, 60 * 1, () => this.http.get('/packages/list')),
    );
  },
  async updatePackage(req: Package) {
    req = PackageHelper.Format(req);
    return dataOnly(
      await this.http.post(
        '/admin/packages/:id/update'.replace(':id', String(req.id)), req
      )
    );
  },

  async getPackages(req?: object) {
    try {
      const orderInfo: GetOrderInfoResponse = await this.getOrderInfo();
      return orderInfo.packages;
    } catch (err) {
      throw err;
    }
  },

  async listBundles() {
    const key = '/admin/bundle/list';
    return dataOnly(
      await cache(key, 60 * 1, () => this.http.get('/admin/bundle/list')),
    );
  },
  async listBundlesNoCache() {
    const key = '/admin/bundle/list';
    return dataOnly(await this.http.get('/admin/bundle/list'));
  },
  async updateBundle(req: Bundle) {
    return dataOnly(
      await this.http.post(
        '/admin/bundle/:id/update'.replace(':id', String(req.id)), req
      )
    );
  },

  async listCoupons() {
    return dataOnly(await this.http.get('/order_coupon/list'));
  },
  async updateCoupon(id: number, coupon: Coupon) {
    return dataOnly(await this.http.post(
      '/order_coupon/:id/update'.replace(':id', String(id)),
      // for some reason available sometimes is a string, force Number!
      Object.assign(coupon, { available: +coupon.available })
    ));
  },
  async createCoupon(coupon: Coupon) {
    return dataOnly(
      await this.http.post(
        '/order_coupon/new',
        // for some reason available sometimes is a string, force Number!
        Object.assign(coupon, { available: +coupon.available })
      )
    );
  },

  async createOrder(req: Order) {
    return dataOnly(
      await this.http.post('/admin/order/new', req)
    );
  },
  async updateOrder(id: number, order: Order) {
    console.log("=== updating order:", order);
    if (order.requested_due_date && Number(order.requested_due_date) < 100) { order.requested_due_date = undefined; }
    return dataOnly(
      await this.http.post('/admin/order/:id/update'.replace(':id', String(id)), order)
    );
  },
  async checkoutOrder(id: number, order: Order) {
    if (order.requested_due_date && Number(order.requested_due_date) < 100) { order.requested_due_date = undefined; }
    return dataOnly(
      await this.http.post('/admin/order/:id/checkout'.replace(':id', String(id)), order)
    );
  },
  async getOrder(id: number) {
    return dataOnly(
      await this.http.get('/admin/order/:id'.replace(':id', String(id)))
    );
  },
  async listOrdersForAccount(accountID: number) {
    return dataOnly(
      await this.http.get('/admin/order/list')
    );
  },
  async listInvoicesForAccount(accountID: number) {
    return dataOnly(
      await this.http.get('/admin/invoice/list')
    );
  },


  async getViews() {
    const key = "admin-views";
    return dataOnly(
      await cache(key, 60 * 5, () => this.http.get('/admin/view/list'))
    );
  },
  async updateView(view: View) {
    const key = "admin-views";
    delCache(key);  // invalidate cache
    return dataOnly(await this.http.post('/admin/view/update', view));
  },
  async deleteView(view: View) {
    const key = "admin-views";
    delCache(key);  // invalidate cache
    return dataOnly(await this.http.post('/admin/view/delete', view));
  },


  wait(dur: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, dur);
    });
  },

  lastAllCall: new Date(),
  async ListPivotAll(req: DataReq) {
    const theCall = new Date();
    this.lastAllCall = theCall;
    await this.wait(200);
    if (this.lastAllCall !== theCall) { return []; }
    return dataOnly(
      await this.http.post('/admin/pivot/list', req)
    );
  },

  async ListOrders(req: DataReq) {
    return dataOnly(
      await this.http.post('/admin/order/list', req)
    );
  },

  async ListInvoices(req: DataReq) {
    return dataOnly(
      await this.http.post('/admin/invoice/list', req)
    );
  },

  async ListSubscriptions(req: DataReq) {
    return dataOnly(
      await this.http.post('/admin/subscription/list', req)
    );
  },

  async ListOrderInvoices(orderID: number) {
    return dataOnly(
      await this.http.get(`/admin/order/${orderID}/invoices`)
    );
  },

  async getOrderShippingQuotes(req: Order) {
    return dataOnly(
      await this.http.post('/admin/shipping/quote', req)
    );
  },

  async resendEmail(id: number) {
    return dataOnly(
      await this.http.post('/admin/communication/resend_email', { id })
    );
  },

  async downloadOrderQuote(orderID: number) {
    const data = await this.http.request({
      url: `/admin/order/${orderID}/quote_pdf`,
      method: 'GET',
      responseType: 'blob'
    });
    const fileName = `QU${orderID}-QUOTE.pdf`;
    SaveDataFile(data.data, fileName, "application/pdf");
  },

  async listServiceMatrices(all: boolean = false) {
    return dataOnly(
      await this.http.get('/admin/service_matrix/list' + (all ? '-all' : ''))
    );
  },

  async listWarehouses() {
    return dataOnly(
      await this.http.get('/admin/warehouse/list')
    );
  },

  async listWarehousePackages() {
    return dataOnly(
      await this.http.get('/admin/warehousepackage/list')
    );
  },

  async toggleWarehousePackage(warehouseID: number, packageShortCode: string, enabled: boolean) {
    return dataOnly(
      await this.http.post('/admin/warehousepackage/toggle', { warehouse_id: warehouseID, package_short_code: packageShortCode, enabled })
    );
  },

  async listAllUserPermissions(): Promise<Permission[]> {
    return dataOnly(
      await this.http.get('/admin/permission/list')
    );
  },
  async setUserPermission(account_id: number, permission: string, canRead: boolean, canWrite: boolean, canDelete: boolean) {
    return dataOnly(
      await this.http.post('/admin/permission/update', {
        account_id,
        permission,
        can_read: canRead,
        can_write: canWrite,
        can_delete: canDelete
      })
    );
  },
  async listServiceMatricesUsers(): Promise<ServiceMatrixUser[]> {
    return dataOnly(
      await this.http.get('/admin/service_matrix/list-service-matrices-users')
    );
  },
  async updateServiceMatrix(matrix_id: number, active: boolean): Promise<ServiceMatrix[]> {
    return dataOnly(
      await this.http.post(`/admin/service_matrix/${matrix_id}/update`, {
        id: matrix_id,
        active: !!active,
      })
    );
  },
  async addServiceMatricesUser(service_matrix_id: number, account_id: number): Promise<ServiceMatrixUser[]> {
    return dataOnly(
      await this.http.post('/admin/service_matrix/add-service-matrix-user', {
        account_id,
        service_matrix_id
      })
    );
  },
  async removeServiceMatricesUser(service_matrix_id: number, account_id: number): Promise<ServiceMatrixUser[]> {
    return dataOnly(
      await this.http.post('/admin/service_matrix/remove-service-matrix-user', {
        account_id,
        service_matrix_id
      })
    );
  },

  async listAllDynamicMails(): Promise<Permission[]> {
    return dataOnly(
      await this.http.get('/admin/dynamicmails/list')
    );
  },


};

export interface GetOrderInfoResponse {
  packages: Package[];
  last_order: Order;
  // prefer_currency: string;
  // exchange_rates: ExchangeRates;
  // token_valid: boolean;
}



